// Org-configured color → token ramp hue (#11426, CD org-color ruling).
//
// The Settings editor stores a hex per enum value, and the earlier keeper
// ruling stands: that hex never reaches paint. It is read as a HUE REQUEST
// and snapped to the nearest hue of the token ramp in OKLCH hue-angle
// distance — lightness and saturation stay the ramp's designed values, so
// contrast floors and the dark theme survive whatever the org picked.
//
// The snap targets all NINE token hues, including red, orange and pink,
// which the DERIVED hash ramp (tagHue.ts) deliberately excludes: a hue a
// hash lands on carries a verdict nobody made, but an org-configured red is
// the org's own judgement — which is the point of honoring it.
//
// Achromatic picks — chroma below the slate token's own (~0.03), which also
// covers near-white and near-black — resolve to slate. Ties resolve to the
// lower ramp index. Collisions are allowed and never auto-nudged (CD:
// distinctness is the picker's job; an invisible nudge map is the defect
// this replaces).

import type { TagHue } from "./tagHue";

export type RampHue = TagHue | "orange" | "red" | "pink";

/** All nine token hues, in the stable order ties resolve toward. */
export const RAMP_HUES: readonly RampHue[] = [
  "blue",
  "teal",
  "green",
  "amber",
  "orange",
  "red",
  "purple",
  "pink",
  "slate",
];

// OKLCH hue angle (degrees) of each chromatic ramp token's light-theme
// anchor — measured from the `--fp-tag-*` values in tokens.css, which stays
// the only file that names the colors themselves (rawLiterals.test.ts). The
// angle is the only property of the anchor the snap consumes; re-measure
// here if a ramp anchor in tokens.css ever moves.
const ANCHOR_HUES: ReadonlyArray<{ hue: RampHue; angle: number }> = [
  { hue: "blue", angle: 258.4 },
  { hue: "teal", angle: 184.2 },
  { hue: "green", angle: 146.9 },
  { hue: "amber", angle: 75.1 },
  { hue: "orange", angle: 47.1 },
  { hue: "red", angle: 22.3 },
  { hue: "purple", angle: 292.8 },
  { hue: "pink", angle: 350.6 },
];

// The slate token's own chroma is ~0.027; anything at or below that band
// carries no legible hue and reads as a grey pick.
const ACHROMATIC_CHROMA = 0.03;

/** An sRGB channel (0–255) as linear light (0–1). */
export const srgbToLinear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

/** #rgb / #rrggbb → [r, g, b] in 0–255, or null when not a hex color. */
export const parseHex = (value: string): [number, number, number] | null => {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const body = match[1];
  const full =
    body.length === 3
      ? body
          .split("")
          .map((c) => c + c)
          .join("")
      : body;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

/** OKLab lightness, a and b of an sRGB color (standard Björn Ottosson fit). */
export const oklab = (
  rgb: readonly [number, number, number]
): [number, number, number] => {
  const r = srgbToLinear(rgb[0]);
  const g = srgbToLinear(rgb[1]);
  const b = srgbToLinear(rgb[2]);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
};

/** OKLab chroma + hue angle of an sRGB color. */
const oklchOf = (rgb: [number, number, number]): { c: number; h: number } => {
  const [, a, bb] = oklab(rgb);
  const c = Math.hypot(a, bb);
  let h = (Math.atan2(bb, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { c, h };
};

const circularDistance = (a: number, b: number): number => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

/**
 * The ramp hue an org-configured color resolves to, or null when the stored
 * value is not a parseable hex (callers fall back to the un-configured
 * behavior rather than guess).
 */
export function snapHexToRampHue(value: string): RampHue | null {
  const rgb = parseHex(value);
  if (!rgb) return null;
  const { c, h } = oklchOf(rgb);
  if (c < ACHROMATIC_CHROMA) return "slate";
  let best: RampHue = ANCHOR_HUES[0].hue;
  let bestDistance = Infinity;
  for (const anchor of ANCHOR_HUES) {
    const distance = circularDistance(h, anchor.angle);
    // Strict < keeps the earlier (lower-index) anchor on an exact tie.
    if (distance < bestDistance) {
      bestDistance = distance;
      best = anchor.hue;
    }
  }
  return best;
}
